import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  useTheme,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material';
import {
  Bolt,
  TrendingUp,
  ArrowForward,
  PlayArrow,
  FolderSpecial,
} from '@mui/icons-material';
import { apiService } from '../services/api';

const Hero: React.FC = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const device = typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop';

    if (!email || !email.includes('@')) {
      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupEmptyAttempt', { source: 'hero', device });
      }
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      await apiService.createBetaSignup({
        email,
        signup_source: 'hero',
      });

      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupSubmitted', {
          source: 'hero',
          device,
          status: 'success'
        });
      }

      setEmail('');
      setShowSuccess(true);
    } catch (error) {
      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupFailed', { source: 'hero', device, error: 'api_error' });
      }
      setErrorMessage('Failed to join waitlist. Please try again.');
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToSection = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      component="section"
      aria-labelledby="hero-title"
      sx={{
        pt: { xs: 14, md: 18 },
        pb: { xs: 10, md: 14 },
        position: 'relative',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(4, 120, 87, 0.05) 0%, rgba(255, 255, 255, 1) 75%)',
        overflow: 'hidden',
      }}
    >
      {/* Background Grid */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 23, 42, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
          opacity: 0.6,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Ticker Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Chip
            icon={<Bolt sx={{ color: '#047857 !important' }} />}
            label="FI-MOD PLATFORM • 1D & 2D SENSITIVITY ENGINE • UK INSTITUTIONAL READY"
            sx={{
              bgcolor: 'rgba(4, 120, 87, 0.08)',
              border: '1px solid rgba(4, 120, 87, 0.2)',
              color: '#047857',
              px: 1.5,
              py: 0.75,
              fontWeight: 700,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              letterSpacing: '0.05em',
            }}
          />
        </Box>

        <Grid container spacing={6} alignItems="center">
          {/* Left Copy */}
          <Grid item xs={12} lg={6}>
            <Box>
              <Typography
                id="hero-title"
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.25rem', md: '3.75rem' },
                  fontWeight: 800,
                  mb: 3,
                  color: '#0f172a',
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                }}
              >
                UK Real Estate Financial Intelligence, Accelerated.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  color: '#475569',
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  lineHeight: 1.65,
                }}
              >
                Institutional-grade financial modelling & model version management for UK real estate private equity, property developers, and debt lenders. Execute 1D & 2D sensitivity matrices, macro scenario stress tests, and centralized underwriting revision tracking.
              </Typography>

              {/* Feature Badges */}
              <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  { icon: <Bolt sx={{ color: '#047857' }} />, text: '1D & 2D Sensitivity Matrix' },
                  { icon: <TrendingUp sx={{ color: '#1e40af' }} />, text: 'Macro Scenario Stressing' },
                  { icon: <FolderSpecial sx={{ color: '#6d28d9' }} />, text: 'Model Revision Control' },
                ].map((item, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {item.icon}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {item.text}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {/* Waitlist Card */}
              <Card
                sx={{
                  p: 3,
                  mb: 3,
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Typography variant="h2" component="h2" sx={{ mb: 1.5, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                  Request Priority Beta Access
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      required
                      inputProps={{ 'aria-label': 'Email Address' }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      endIcon={!isLoading && <ArrowForward />}
                      sx={{
                        px: 3.5,
                        py: 1.5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Get Early Access'}
                    </Button>
                  </Box>
                </form>
                {errorMessage && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'error.main' }}>
                    {errorMessage}
                  </Typography>
                )}
              </Card>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  component="a"
                  href="#sensitivity-section"
                  variant="outlined"
                  onClick={(e) => scrollToSection(e, 'sensitivity-section')}
                  startIcon={<PlayArrow sx={{ color: '#047857' }} />}
                  sx={{
                    borderColor: 'rgba(4, 120, 87, 0.3)',
                    color: '#047857',
                    textDecoration: 'none',
                    '&:hover': { borderColor: '#047857', bgcolor: 'rgba(4, 120, 87, 0.05)' },
                  }}
                >
                  Test 2D Sensitivity Demo
                </Button>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  • Private Beta Launching Q4 2026
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right Side Terminal Preview Card */}
          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                p: 3,
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.08)',
                borderRadius: 4,
              }}
            >
              {/* Titlebar */}
              <Box
                sx={{
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                  pb: 2,
                  mb: 2.5,
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                  <Typography variant="caption" sx={{ color: '#64748b', ml: 1, fontFamily: 'monospace', fontWeight: 700 }}>
                    fimod_terminal (London EC2 Asset)
                  </Typography>
                </Box>
                <Chip
                  label="CALC ENGINE: ACTIVE"
                  size="small"
                  sx={{ bgcolor: 'rgba(4, 120, 87, 0.08)', color: '#047857', fontWeight: 700, fontSize: '0.7rem' }}
                />
              </Box>

              {/* Asset Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 700, letterSpacing: '0.05em' }}>
                  UNDERWRITING MODEL #UK-8842
                </Typography>
                <Typography variant="h3" component="h3" sx={{ color: '#0f172a', fontWeight: 800, fontSize: '1.25rem', mb: 0.5 }}>
                  100 Bishopsgate, London EC2M
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Mixed-Use Commercial & Retail | 99,000 sq ft | Model v3.2 IC Final
                </Typography>
              </Box>

              {/* Key Metrics */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>EXIT GDV</Typography>
                    <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800, fontFamily: 'monospace' }}>£52.4M</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(4, 120, 87, 0.06)', border: '1px solid rgba(4, 120, 87, 0.2)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#047857', display: 'block', fontWeight: 700 }}>LEVERED IRR</Typography>
                    <Typography variant="h6" sx={{ color: '#047857', fontWeight: 800, fontFamily: 'monospace' }}>18.2%</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(30, 64, 175, 0.06)', border: '1px solid rgba(30, 64, 175, 0.2)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', fontWeight: 700 }}>DSCR COVER</Typography>
                    <Typography variant="h6" sx={{ color: '#1e40af', fontWeight: 800, fontFamily: 'monospace' }}>1.65x</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(109, 40, 217, 0.06)', border: '1px solid rgba(109, 40, 217, 0.2)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#6d28d9', display: 'block', fontWeight: 700 }}>UK SDLT</Typography>
                    <Typography variant="h6" sx={{ color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace' }}>£2.35M</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Console Output */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#0f172a',
                  borderRadius: 2,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.78rem',
                  color: '#94a3b8',
                }}
              >
                <Box sx={{ color: '#34d399', mb: 0.5 }}>✓ Underwriting Model v3.2 synchronized</Box>
                <Box sx={{ color: '#60a5fa', mb: 0.5 }}>✓ UK SDLT calculated: Commercial slab @ 5% = £2.35M</Box>
                <Box sx={{ color: '#fbbf24', mb: 0.5 }}>⚡ 2D Sensitivity Matrix: 25 cap/growth permutations calculated</Box>
                <Box sx={{ color: '#ffffff' }}>&gt; Model Status: Underwriting Approved • Audit History Synced</Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ bgcolor: '#047857', color: '#ffffff', fontWeight: 700 }}>
          Thank you! You are on the FiMod UK beta priority list.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Hero;
